import { Focus, Layers, Share2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import type { PopoverAnchor } from '../../hooks';
import { dataContextChipLabel } from '../../lib/data-context-label';
import { UserTypeDrillButton, type IdentityKind } from '../../lib/user-type-drill';
import { cn } from '../../lib/utils';
import { BreadcrumbTagRow, type BreadcrumbRelatedTag } from '../../molecules/BreadcrumbTagRow';
import { DataContextColumn, type DataContextColumnRow } from '../../molecules/DataContextColumn';
import { FlowLayer, pipeKey, type FlowConnection } from '../../molecules/FlowLayer';
import { IdentityColumn, type IdentityColumnRow } from '../../molecules/IdentityColumn';
import { MiniMetricItem } from '../../molecules/MiniMetricItem';
import { PipePopover, type PipeBreakdownItem } from '../../molecules/PipePopover';
import { RowActionPopover, type RowActionItem } from '../../molecules/RowActionPopover';

export interface AccessDiscoveryBodyDefaultData {
  totalFiles: number;
  nhiAccess: number;
  hiAccess: number;
  externalAccess: number;
  identityColumn: readonly IdentityColumnRow[];
  dataContext: readonly DataContextColumnRow[];
  flowConnections: readonly AccessDiscoveryFlow[];
}

/**
 * One tier of the drill, not "the drill-down". `trail` is the path from the
 * department down to the tier being shown, so the number of levels comes from
 * the data rather than from a fixed depth: a department whose document types
 * carry no cluster path has a trail of one, which is what the drill has always
 * rendered.
 */
export interface AccessDiscoveryBodyDrillDownData {
  trail: readonly { id: string; label: string }[];
  categories: readonly DataContextColumnRow[];
  flowConnections: readonly AccessDiscoveryFlow[];
}

export interface AccessDiscoveryFlow extends FlowConnection {
  fileCount: number;
  distinctHICount: number;
  distinctNHICount: number;
  distinctExternalCount: number;
  sensitivityBreakdown: readonly PipeBreakdownItem[];
  regulationBreakdown: readonly PipeBreakdownItem[];
}

export interface ShowAccessEventsContext {
  /** Identity id when the action originated from an identity surface; null otherwise. */
  identityId: string | null;
  /** Data-context id when the action originated from a data surface; null otherwise. */
  dataContextId: string | null;
  /** True when the surface was the drill-down view. */
  isDrillDown: boolean;
}

const NO_IDS: ReadonlySet<string> = new Set();

export interface AccessDiscoveryBodyProps {
  defaultData: AccessDiscoveryBodyDefaultData;
  drillDownData: AccessDiscoveryBodyDrillDownData | null;
  /** Ids that offer Drill Down, at any depth; a node with a tier beneath it may be left out. */
  drillableIds: ReadonlySet<string>;
  /** Ids no event filter can name, such as folds ("All Other"). Show Access Events is withheld
   * on these rows and on any pipe touching one. Defaults to none. */
  noEventsIds?: ReadonlySet<string>;
  /** Icon nodes for the four mini-metrics; supplied by the wrapper. */
  totalFilesIcon: ReactNode;
  nhiAccessIcon: ReactNode;
  hiAccessIcon: ReactNode;
  externalAccessIcon: ReactNode;
  /** Callback fired when the user drills into a row, at any depth. */
  onDrillDown: (nodeId: string) => void;
  /**
   * Callback fired when the user leaves one or more tiers. `toDepth` is the
   * trail length to keep: 0 returns to the departments, 1 keeps the department
   * and drops everything below it.
   */
  onExitDrillDown: (toDepth: number) => void;
  /** Callback fired when the user clicks "Show Access Events" anywhere in the widget. */
  onShowAccessEvents: (context: ShowAccessEventsContext) => void;
  /**
   * An NHI / HI / External count click (TDEV-1116): `nodeId` is the data row in
   * the tier being shown, or null for the header mini-metrics. When supplied,
   * those counts are buttons; a fold row's counts stay plain text.
   */
  onUserTypeClick?: (nodeId: string | null, kind: IdentityKind) => void;
  formatNumber: (value: number) => string;
  /**
   * Storybook-only override that bypasses the hover debounce. Production wrappers
   * never pass it.
   */
  forceHoverOpen?:
    | { kind: 'identityRow'; id: string }
    | { kind: 'dataContextRow'; id: string }
    | { kind: 'pipe'; fromId: string; toId: string };
  /**
   * Storybook-only override that writes hovered{Identity,DataContext}Id directly,
   * bypassing the hover debounce. Production wrappers never pass it.
   */
  forceHoverPreview?: { kind: 'identity'; id: string } | { kind: 'dataContext'; id: string };
  className?: string;
}

type FocusOrigin = 'identity' | 'dataContext' | null;

interface PopoverState {
  kind: 'identityRow' | 'dataContextRow' | 'pipe';
  id?: string;
  fromId?: string;
  toId?: string;
  /** The hovered row's label element, or the cursor point on a pipe. */
  anchor: PopoverAnchor;
}

/**
 * Grace window after the cursor leaves the bar or the popover before the
 * popover (and any hover-preview dim) actually closes. Lets the user move
 * across the small gap between bar and popover without losing the popover.
 */
const POPOVER_CLOSE_GRACE_MS = 150;

export function AccessDiscoveryBody({
  defaultData,
  drillDownData,
  drillableIds,
  totalFilesIcon,
  nhiAccessIcon,
  hiAccessIcon,
  externalAccessIcon,
  onDrillDown,
  onExitDrillDown,
  onShowAccessEvents,
  onUserTypeClick,
  noEventsIds = NO_IDS,
  formatNumber,
  forceHoverOpen,
  forceHoverPreview,
  className,
}: AccessDiscoveryBodyProps) {
  const [focusedIdentityId, setFocusedIdentityId] = useState<string | null>(null);
  const [focusedDataContextId, setFocusedDataContextId] = useState<string | null>(null);
  const [focusOrigin, setFocusOrigin] = useState<FocusOrigin>(null);
  const [popover, setPopover] = useState<PopoverState | null>(null);
  const [hoveredIdentityId, setHoveredIdentityId] = useState<string | null>(null);
  const [hoveredDataContextId, setHoveredDataContextId] = useState<string | null>(null);

  const identityRowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const dataRowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerReady, setContainerReady] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (containerRef.current) setContainerReady(true);
  }, []);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current !== null) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const cancelScheduledClose = () => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    cancelScheduledClose();
    closeTimerRef.current = setTimeout(() => {
      closePopover();
    }, POPOVER_CLOSE_GRACE_MS);
  };

  /**
   * Closes the popover *and* drops the hover-preview ids. Acting on a popover
   * item unmounts it under the cursor, so no mouseleave ever fires for it and
   * `scheduleClose` never runs — clearing only `popover` would leave the chart
   * dimmed against a row the cursor left long ago.
   */
  const closePopover = () => {
    cancelScheduledClose();
    setPopover(null);
    setHoveredIdentityId(null);
    setHoveredDataContextId(null);
  };

  useEffect(() => {
    if (!forceHoverPreview) return;
    if (forceHoverPreview.kind === 'identity') {
      setHoveredIdentityId(forceHoverPreview.id);
    } else {
      setHoveredDataContextId(forceHoverPreview.id);
    }
  }, [forceHoverPreview]);

  const isDrillDown = drillDownData !== null;

  /**
   * Breadcrumb/popover chrome names a department "<name> Data"; the drill-down
   * rowspace is sub-categories ("Financial Reports"), which are named as-is
   * (Figma `5:19616`).
   */
  const dataContextLabel = (label: string): string =>
    isDrillDown ? label : dataContextChipLabel(label);

  /**
   * Breadcrumb display formatting: the server returns the bare department label
   * (e.g. "Sales") and drill chrome shows it as "Sales Data". Only the
   * department reads that way; a cluster label is already a noun phrase.
   */
  const drillTrail = isDrillDown
    ? drillDownData.trail.map((step, depth) => ({
        id: step.id,
        label: depth === 0 ? dataContextChipLabel(step.label) : step.label,
      }))
    : [];
  const currentTierLabel = drillTrail.length > 0 ? drillTrail[drillTrail.length - 1]!.label : '';

  const activeDataContextRows = isDrillDown ? drillDownData.categories : defaultData.dataContext;
  /** Anchor ordering for `FlowLayer`'s relayout signature — see its `rowOrder` prop. */
  const rowOrder = [
    ...defaultData.identityColumn.map((r) => r.id),
    ...activeDataContextRows.map((r) => r.id),
  ];
  const activeFlowConnections = isDrillDown
    ? drillDownData.flowConnections
    : defaultData.flowConnections;

  const handleIdentityFocus = (id: string) => {
    setFocusedIdentityId(id);
    setFocusedDataContextId(null);
    setFocusOrigin('identity');
    closePopover();
  };

  const handleIdentityUnfocus = () => {
    // Unfocus from the focused row's main label exits focus entirely. In
    // two-axis mode that means we also drop the data-context axis.
    setFocusedIdentityId(null);
    if (focusedDataContextId !== null) setFocusedDataContextId(null);
    setFocusOrigin(null);
    closePopover();
  };

  const handleDataContextFocus = (id: string) => {
    setFocusedDataContextId(id);
    setFocusedIdentityId(null);
    setFocusOrigin('dataContext');
    closePopover();
  };

  const handleDataContextUnfocus = () => {
    // Symmetric to handleIdentityUnfocus: exit focus entirely in two-axis.
    setFocusedDataContextId(null);
    if (focusedIdentityId !== null) setFocusedIdentityId(null);
    setFocusOrigin(null);
    closePopover();
  };

  /**
   * Pipe-popover Focus: narrows the chart to the single pipe by focusing both
   * ends without clearing the existing focus origin. See design.md "Two-axis
   * focus" — clicking Focus from a pipe popover sets the *missing* axis.
   */
  const handlePipeFocus = (fromId: string, toId: string) => {
    setFocusedIdentityId(fromId);
    setFocusedDataContextId(toId);
    if (focusOrigin === null) setFocusOrigin('identity');
    closePopover();
  };

  /**
   * Pipe-popover Unfocus: reverts two-axis focus back to the prior single-axis
   * state. focusOrigin tells us which axis was focused first; we drop only the
   * axis that pipe-Focus added.
   */
  const handlePipeUnfocus = () => {
    if (focusOrigin === 'identity') {
      setFocusedDataContextId(null);
    } else {
      setFocusedIdentityId(null);
    }
    closePopover();
  };

  /**
   * Leaving a tier replaces the rowspace under the focus, so a focused id only
   * survives when it is a row of the tier being returned to. The departments
   * are the one tier this component can see, so any other target clears it.
   */
  const handleExitDrillTo = (toDepth: number) => {
    const survives =
      toDepth === 0 &&
      focusedDataContextId !== null &&
      defaultData.dataContext.some((r) => r.id === focusedDataContextId);
    onExitDrillDown(toDepth);
    if (survives) return;
    setFocusedDataContextId(null);
    // Origin only means something while two axes are focused, and leaving it set
    // changes which axis the next pipe Unfocus drops.
    setFocusOrigin(focusedIdentityId === null ? null : 'identity');
  };

  /**
   * A row click toggles focus on the row under the pointer, so that row's menu
   * stays open and re-derives Focus ↔ Unfocus. Closing it would leave no menu at
   * all: the row is still hovered, so its debounce never fires again until the
   * pointer leaves and re-enters. A menu item's own Focus still closes the menu.
   */
  const handleIdentityRowClick = (id: string) => {
    const menu = popover?.kind === 'identityRow' && popover.id === id ? popover : null;
    if (focusedIdentityId === id) handleIdentityUnfocus();
    else handleIdentityFocus(id);
    if (menu) setPopover(menu);
  };

  const handleDataContextRowClick = (id: string) => {
    const menu = popover?.kind === 'dataContextRow' && popover.id === id ? popover : null;
    if (focusedDataContextId === id) handleDataContextUnfocus();
    else handleDataContextFocus(id);
    if (menu) setPopover(menu);
  };

  /**
   * A focused id belongs to the rowspace under it. Descending and exiting clear it
   * explicitly, but a tier that vanishes from the data goes through neither: the
   * rowspace is replaced while the id stays set, which keeps isFocusMode true and
   * leaves the new tier's hover previews disarmed.
   */
  useEffect(() => {
    if (focusedDataContextId === null) return;
    if (activeDataContextRows.some((row) => row.id === focusedDataContextId)) return;
    setFocusedDataContextId(null);
    setFocusOrigin(focusedIdentityId === null ? null : 'identity');
    setHoveredDataContextId(null);
    setPopover(null);
  }, [activeDataContextRows, focusedDataContextId, focusedIdentityId]);

  const isFocusMode = focusedIdentityId !== null || focusedDataContextId !== null;
  const isTwoAxisFocus = focusedIdentityId !== null && focusedDataContextId !== null;

  /**
   * Descending clears both axes.
   *
   * The data-context focus cannot survive: the focused row is the node being
   * drilled into or one of its siblings, and neither is a row of the tier below.
   * Left set it keeps `isFocusMode` true and the new tier's hover previews never
   * arm.
   *
   * The identity focus goes too, for two reasons. `BreadcrumbTagRow` renders
   * `identityFocus` before the drill arm, so an identity focus carried into a
   * drill hides the trail, the back-link and every tier dismiss, leaving no way
   * out. And the drill's specified appearance is the nothing-focused one, where
   * identity rows dim by whether they have an edge into the tier (Figma
   * `5:19809`); holding a focus means that view never renders.
   */
  const handleDrillInto = (id: string) => {
    onDrillDown(id);
    setFocusedDataContextId(null);
    setFocusedIdentityId(null);
    setFocusOrigin(null);
    closePopover();
  };

  const handleIdentityRowHoverEnter = (id: string, anchorEl: HTMLElement) => {
    if (forceHoverOpen?.kind === 'identityRow' && forceHoverOpen.id !== id) return;
    // Two-axis: chart is narrowed to a single pipe; the pipe popover is the
    // only navigation surface, so row hovers don't open their own popover.
    if (isTwoAxisFocus) return;
    cancelScheduledClose();
    setPopover({ kind: 'identityRow', id, anchor: anchorEl });
    if (!isFocusMode) {
      setHoveredIdentityId(id);
    }
  };

  const handleIdentityRowHoverLeave = () => {
    scheduleClose();
  };

  const handleDataContextRowHoverEnter = (id: string, anchorEl: HTMLElement) => {
    if (forceHoverOpen?.kind === 'dataContextRow' && forceHoverOpen.id !== id) return;
    if (isTwoAxisFocus) return;
    cancelScheduledClose();
    setPopover({ kind: 'dataContextRow', id, anchor: anchorEl });
    if (!isFocusMode) {
      setHoveredDataContextId(id);
    }
  };

  const handleDataContextRowHoverLeave = () => {
    scheduleClose();
  };

  const handlePipeHoverEnter = (fromId: string, toId: string, anchorRect: DOMRect) => {
    cancelScheduledClose();
    setPopover({ kind: 'pipe', fromId, toId, anchor: anchorRect });
  };

  const handlePipeHoverLeave = () => {
    scheduleClose();
  };

  const getIdentityActions = (id: string): readonly RowActionItem[] => {
    const isFocused = focusedIdentityId === id;
    const actions: RowActionItem[] = [
      {
        id: 'focus',
        icon: Focus,
        label: isFocused ? 'Unfocus' : 'Focus',
        onClick: () => {
          if (isFocused) handleIdentityUnfocus();
          else handleIdentityFocus(id);
        },
      },
    ];
    if (!noEventsIds.has(id)) {
      actions.push({
        id: 'events',
        icon: Share2,
        label: 'Show Access Events',
        onClick: () => {
          onShowAccessEvents({ identityId: id, dataContextId: null, isDrillDown });
          closePopover();
        },
      });
    }
    return actions;
  };

  const getDataContextActions = (id: string): readonly RowActionItem[] => {
    const isFocused = focusedDataContextId === id;
    const actions: RowActionItem[] = [
      {
        id: 'focus',
        icon: Focus,
        label: isFocused ? 'Unfocus' : 'Focus',
        onClick: () => {
          if (isFocused) handleDataContextUnfocus();
          else handleDataContextFocus(id);
        },
      },
    ];

    // Every tier drills the same way; a row offers the action when the warehouse
    // published a tier beneath it. The id guard stands alone now that it can:
    // ids are namespaced per tier by the warehouse and injective in (parent,
    // child), so a leaf can never collide with a node that has children.
    if (drillableIds.has(id)) {
      actions.push({
        id: 'drill',
        icon: Layers,
        label: 'Drill Down By Data Categories',
        onClick: () => {
          handleDrillInto(id);
        },
      });
    }

    if (!noEventsIds.has(id)) {
      actions.push({
        id: 'events',
        icon: Share2,
        label: 'Show Access Events',
        onClick: () => {
          onShowAccessEvents({ identityId: null, dataContextId: id, isDrillDown });
          closePopover();
        },
      });
    }

    return actions;
  };

  const getPipeActions = (fromId: string, toId: string): readonly RowActionItem[] => {
    const isPipeFocused = focusedIdentityId === fromId && focusedDataContextId === toId;
    const actions: RowActionItem[] = [
      {
        id: 'focus',
        icon: Focus,
        label: isPipeFocused ? 'Unfocus' : 'Focus',
        onClick: () => {
          if (isPipeFocused) handlePipeUnfocus();
          else handlePipeFocus(fromId, toId);
        },
      },
    ];
    if (!noEventsIds.has(fromId) && !noEventsIds.has(toId)) {
      actions.push({
        id: 'events',
        icon: Share2,
        label: 'Show Access Events',
        onClick: () => {
          onShowAccessEvents({
            identityId: fromId,
            dataContextId: toId,
            isDrillDown,
          });
          closePopover();
        },
      });
    }
    return actions;
  };

  const getIdentityRelatedTags = (): readonly BreadcrumbRelatedTag[] => {
    if (!focusedIdentityId) return [];
    const focusedIdentityLabel =
      defaultData.identityColumn.find((r) => r.id === focusedIdentityId)?.label ??
      focusedIdentityId;
    const relatedConnections = activeFlowConnections.filter((c) => c.fromId === focusedIdentityId);
    const result: BreadcrumbRelatedTag[] = [];
    for (const conn of relatedConnections) {
      if (isTwoAxisFocus && conn.toId !== focusedDataContextId) continue;
      const dcRow = activeDataContextRows.find((r) => r.id === conn.toId);
      if (!dcRow) continue;
      result.push({
        id: dcRow.id,
        label: dataContextLabel(dcRow.label),
        count: conn.fileCount,
        isHighlighted: dcRow.isHighlighted,
        fromLabel: focusedIdentityLabel,
        toLabel: dataContextLabel(dcRow.label),
        actions: getPipeActions(focusedIdentityId, dcRow.id),
      });
    }
    return result;
  };

  const getDataRelatedTags = (): readonly BreadcrumbRelatedTag[] => {
    // Drill-down with nothing focused: the breadcrumb carries one tag per
    // sub-category of the drilled department (chart.md §3 "related-CATEGORY
    // tags", PRD story #36), so the user can act on a category without
    // hunting for its row.
    if (isDrillDown && focusedDataContextId === null) {
      const parentLabel = currentTierLabel;
      return drillDownData.categories.map((category) => ({
        id: category.id,
        label: category.label,
        count: category.files,
        isHighlighted: category.isHighlighted,
        fromLabel: parentLabel,
        toLabel: category.label,
        actions: getDataContextActions(category.id),
      }));
    }

    if (!focusedDataContextId) return [];
    const focusedDataContextLabel = dataContextLabel(
      activeDataContextRows.find((r) => r.id === focusedDataContextId)?.label ??
        focusedDataContextId
    );
    const relatedConnections = activeFlowConnections.filter((c) => c.toId === focusedDataContextId);
    const result: BreadcrumbRelatedTag[] = [];
    for (const conn of relatedConnections) {
      if (isTwoAxisFocus && conn.fromId !== focusedIdentityId) continue;
      const identityRow = defaultData.identityColumn.find((r) => r.id === conn.fromId);
      if (!identityRow) continue;
      result.push({
        id: identityRow.id,
        label: identityRow.label,
        count: conn.fileCount,
        fromLabel: identityRow.label,
        toLabel: focusedDataContextLabel,
        actions: getPipeActions(identityRow.id, focusedDataContextId),
      });
    }
    return result;
  };

  // Fade helpers must be namespace-aware across view-state transitions. The
  // identity column is shared between default and drill-down views, so any
  // identity focus/hover id remains valid in both. The data-context rowspace
  // changes: default view uses parent department ids (e.g. 'financial');
  // drill-down view uses sub-category ids (e.g. 'reports'). When a default-view
  // DC focus/hover persists into drill-down, that id is not present in the
  // drill rowspace and must be ignored — otherwise every connection fails the
  // DC-match test and the entire drill view fades. Fresh DC focus/hover set
  // while in drill-down (sub-category id) is a real intent and must drive
  // fade so drill-down's interactive layer mirrors default-view behavior per
  // design.md §3 "Visual States". The two-axis flag must also be derived from
  // the filtered ids; otherwise a stale parent-id DC focus combined with a
  // fresh identity focus would incorrectly enter two-axis fade mode.
  const isDataContextIdInRowspace = (id: string | null): boolean =>
    id !== null && activeDataContextRows.some((r) => r.id === id);

  interface EffectiveFocus {
    identityId: string | null;
    dataContextId: string | null;
    active: boolean;
    twoAxis: boolean;
  }

  const getEffectiveFocus = (): EffectiveFocus => {
    const rawIdentityId = focusedIdentityId ?? hoveredIdentityId;
    const rawDataContextId = focusedDataContextId ?? hoveredDataContextId;
    const identityId = rawIdentityId; // identity column shared across views
    const dataContextId = isDataContextIdInRowspace(rawDataContextId) ? rawDataContextId : null;
    return {
      identityId,
      dataContextId,
      active: identityId !== null || dataContextId !== null,
      // Two-axis applies only when the chart is narrowed to a single pipe;
      // both axes must be committed via focus (not hover) AND both must be
      // valid in the current rowspace.
      twoAxis: focusedIdentityId !== null && isDataContextIdInRowspace(focusedDataContextId),
    };
  };

  const getFadedIdentityIds = (): Readonly<Record<string, boolean>> => {
    const eff = getEffectiveFocus();
    if (!eff.active) {
      if (!isDrillDown) return {};
      // Drill-down, nothing focused: an identity with no edge into the drilled
      // department's categories is unrelated to what the user came here to see,
      // so it dims (Figma `5:19809` renders 4 of 10 bars at full strength).
      // design.md's "identity column is byte-identical" still holds — order,
      // labels, colors and bar heights are untouched; only opacity differs.
      const drillResult: Record<string, boolean> = {};
      for (const row of defaultData.identityColumn) {
        drillResult[row.id] = !activeFlowConnections.some((c) => c.fromId === row.id);
      }
      return drillResult;
    }
    const result: Record<string, boolean> = {};
    for (const row of defaultData.identityColumn) {
      const matchesIdentity = eff.identityId !== null && row.id === eff.identityId;
      const connectsToDataContext =
        eff.dataContextId !== null &&
        activeFlowConnections.some((c) => c.fromId === row.id && c.toId === eff.dataContextId);
      // Two-axis: only the focused identity row stays bright.
      const unfaded = eff.twoAxis ? matchesIdentity : matchesIdentity || connectsToDataContext;
      result[row.id] = !unfaded;
    }
    return result;
  };

  const getFadedDataContextIds = (): Readonly<Record<string, boolean>> => {
    const eff = getEffectiveFocus();
    if (!eff.active) return {};
    // In drill-down mode the right column is the user's chosen breakdown — they
    // came here to see *all* sub-categories of the parent department. Focusing
    // a sub-category should highlight it (and dim the unrelated identity rows
    // and flow lines), but the other sub-category rows must stay visible.
    if (isDrillDown) return {};
    const result: Record<string, boolean> = {};
    for (const row of activeDataContextRows) {
      const matchesDataContext = eff.dataContextId !== null && row.id === eff.dataContextId;
      const receivesFromIdentity =
        eff.identityId !== null &&
        activeFlowConnections.some((c) => c.fromId === eff.identityId && c.toId === row.id);
      // Two-axis: only the focused data-context row stays bright.
      const unfaded = eff.twoAxis ? matchesDataContext : matchesDataContext || receivesFromIdentity;
      result[row.id] = !unfaded;
    }
    return result;
  };

  const getFadedPipeKeys = (): ReadonlySet<string> => {
    const eff = getEffectiveFocus();
    if (!eff.active) return new Set();
    const result = new Set<string>();
    for (const conn of activeFlowConnections) {
      const matchesIdentity = eff.identityId !== null && conn.fromId === eff.identityId;
      const matchesDataContext = eff.dataContextId !== null && conn.toId === eff.dataContextId;
      // Two-axis: only the single AND-match pipe stays bright.
      const unfaded = eff.twoAxis
        ? matchesIdentity && matchesDataContext
        : matchesIdentity || matchesDataContext;
      if (!unfaded) {
        result.add(pipeKey(conn.fromId, conn.toId));
      }
    }
    return result;
  };

  const activePopover = (() => {
    if (forceHoverOpen) {
      if (forceHoverOpen.kind === 'identityRow') {
        const id = forceHoverOpen.id;
        const anchor = identityRowRefs.current[id] ?? new DOMRect();
        return { kind: 'identityRow' as const, id, anchor };
      }
      if (forceHoverOpen.kind === 'dataContextRow') {
        const id = forceHoverOpen.id;
        const anchor = dataRowRefs.current[id] ?? new DOMRect();
        return { kind: 'dataContextRow' as const, id, anchor };
      }
      if (forceHoverOpen.kind === 'pipe') {
        const { fromId, toId } = forceHoverOpen;
        return { kind: 'pipe' as const, fromId, toId, anchor: new DOMRect() };
      }
    }
    return popover;
  })();

  const renderPopover = () => {
    if (!activePopover) return null;

    if (activePopover.kind === 'identityRow' && activePopover.id) {
      const id = activePopover.id;
      const identityRow = defaultData.identityColumn.find((r) => r.id === id);
      if (!identityRow) return null;
      return (
        <RowActionPopover
          heading={identityRow.label}
          actions={getIdentityActions(id)}
          anchor={activePopover.anchor}
          onMouseEnter={cancelScheduledClose}
          onMouseLeave={scheduleClose}
        />
      );
    }

    if (activePopover.kind === 'dataContextRow' && activePopover.id) {
      const id = activePopover.id;
      const dcRow = activeDataContextRows.find((r) => r.id === id);
      if (!dcRow) return null;
      return (
        <RowActionPopover
          heading={dataContextLabel(dcRow.label)}
          actions={getDataContextActions(id)}
          anchor={activePopover.anchor}
          onMouseEnter={cancelScheduledClose}
          onMouseLeave={scheduleClose}
        />
      );
    }

    if (activePopover.kind === 'pipe' && activePopover.fromId && activePopover.toId) {
      const { fromId, toId } = activePopover;
      const identityRow = defaultData.identityColumn.find((r) => r.id === fromId);
      const dcRow = activeDataContextRows.find((r) => r.id === toId);
      const conn = activeFlowConnections.find((c) => c.fromId === fromId && c.toId === toId) as
        | AccessDiscoveryFlow
        | undefined;
      if (!identityRow || !dcRow || !conn) return null;
      return (
        <PipePopover
          fromLabel={identityRow.label}
          toLabel={dataContextLabel(dcRow.label)}
          counts={{
            files: conn.fileCount,
            nhi: conn.distinctNHICount,
            hi: conn.distinctHICount,
            external: conn.distinctExternalCount,
          }}
          sensitivityBreakdown={conn.sensitivityBreakdown}
          regulationBreakdown={conn.regulationBreakdown}
          formatNumber={formatNumber}
          actions={getPipeActions(fromId, toId)}
          anchor={activePopover.anchor}
          onMouseEnter={cancelScheduledClose}
          onMouseLeave={scheduleClose}
        />
      );
    }

    return null;
  };

  const headerMetric = (kind: IdentityKind, metric: JSX.Element): JSX.Element =>
    onUserTypeClick ? (
      <UserTypeDrillButton kind={kind} onClick={() => onUserTypeClick(null, kind)}>
        {metric}
      </UserTypeDrillButton>
    ) : (
      metric
    );

  const dataFocusProp = (() => {
    if (isDrillDown) {
      // Sub-category focus is committed via focusedDataContextId; the drill's
      // rowspace is the current tier, so a focused id resolves to one of its rows.
      const focusedSub =
        focusedDataContextId !== null
          ? activeDataContextRows.find((r) => r.id === focusedDataContextId)
          : null;
      return {
        kind: 'drilldown' as const,
        trail: drillTrail,
        focusedSubCategory: focusedSub ? { id: focusedSub.id, label: focusedSub.label } : null,
      };
    }
    if (focusedDataContextId) {
      const dcRow = defaultData.dataContext.find((r) => r.id === focusedDataContextId);
      return {
        kind: 'focus' as const,
        id: focusedDataContextId,
        label: dataContextLabel(dcRow?.label ?? focusedDataContextId),
      };
    }
    return { kind: 'static' as const };
  })();

  return (
    <div
      data-testid="access-discovery-body"
      className={cn('relative flex flex-col gap-6 font-outfit text-dawn-black', className)}
    >
      {renderPopover()}

      <BreadcrumbTagRow
        identityFocus={
          focusedIdentityId
            ? {
                id: focusedIdentityId,
                label:
                  defaultData.identityColumn.find((r) => r.id === focusedIdentityId)?.label ??
                  focusedIdentityId,
              }
            : null
        }
        dataFocus={dataFocusProp}
        identityRelated={getIdentityRelatedTags()}
        dataRelated={getDataRelatedTags()}
        formatNumber={formatNumber}
        onDismissIdentityFocus={() => {
          setFocusedIdentityId(null);
          if (isTwoAxisFocus) setFocusedDataContextId(null);
          setFocusOrigin(null);
        }}
        // One prop, two meanings: in drill-down it is the back-link out of the
        // drill; in plain focus it drops the data-context focus. Sending both
        // through the drill-down handler made the focus tag's X dead — its
        // guard only clears a focused id that is absent from the default rows,
        // which is never true for a real department.
        onDismissDataFocus={isDrillDown ? () => handleExitDrillTo(0) : handleDataContextUnfocus}
        onDismissDrillLevel={(depth) => handleExitDrillTo(depth)}
        onDismissSubCategoryFocus={() => {
          setFocusedDataContextId(null);
          if (focusedIdentityId !== null) setFocusedIdentityId(null);
          setFocusOrigin(null);
        }}
        // The root chips are breadcrumb roots: clicking one returns to the
        // unfiltered view of that axis, same as dismissing its focus tag. Wired
        // only while that axis is focused — in the resting state they are
        // labels, and a pointer cursor on a no-op chip promises an action that
        // isn't there. (Drill-down's "All Data" chip is the back-link, handled
        // by onDismissDataFocus.)
        onClickAllDepartments={focusedIdentityId !== null ? handleIdentityUnfocus : undefined}
        onClickAllData={focusedDataContextId !== null ? handleDataContextUnfocus : undefined}
      />

      <div data-testid="mini-metrics-row" className="flex flex-wrap gap-x-10 gap-y-3">
        <MiniMetricItem
          icon={totalFilesIcon}
          stateLabel={formatNumber(defaultData.totalFiles)}
          label="Total Files"
        />
        {headerMetric(
          'NHI',
          <MiniMetricItem
            icon={nhiAccessIcon}
            stateLabel={String(defaultData.nhiAccess)}
            label="NHI Access"
          />
        )}
        {headerMetric(
          'HI',
          <MiniMetricItem
            icon={hiAccessIcon}
            stateLabel={String(defaultData.hiAccess)}
            label="HI Access"
          />
        )}
        {headerMetric(
          'External',
          <MiniMetricItem
            data-testid="external-access-metric"
            icon={externalAccessIcon}
            stateLabel={String(defaultData.externalAccess)}
            label="External Access"
          />
        )}
      </div>

      <div className="flex flex-col">
        <div className="mt-[5px] flex gap-2">
          <div className="w-[160px] shrink-0 text-right 2xl:w-[192px]">
            <span className="inline-block translate-x-[17px] text-[12px] font-medium text-grey-300">
              Identity
            </span>
          </div>
          <div className="flex-1" />
          <div className="mr-2 w-[388px] shrink-0 2xl:mr-[76px]">
            <span
              data-testid="data-column-header"
              className="inline-block -translate-x-8 text-[12px] font-medium text-grey-300"
            >
              {isDrillDown ? 'Data Categories' : 'Data Context'}
            </span>
          </div>
        </div>

        <div ref={containerRef} className="relative mt-[11px] flex items-stretch gap-2">
          {containerReady && (
            <FlowLayer
              connections={activeFlowConnections.map((c) => ({
                ...c,
                fromIndex: Math.max(
                  0,
                  defaultData.identityColumn.findIndex((r) => r.id === c.fromId)
                ),
              }))}
              identityRowRefs={identityRowRefs}
              dataRowRefs={dataRowRefs}
              containerRef={containerRef}
              rowOrder={rowOrder}
              fadedKeys={getFadedPipeKeys()}
              isInteractive
              colorMode={drillDownData == null ? 'sensitivity' : 'identity'}
              onPipeHoverEnter={handlePipeHoverEnter}
              onPipeHoverLeave={handlePipeHoverLeave}
              className="absolute inset-0"
            />
          )}

          <IdentityColumn
            rows={defaultData.identityColumn}
            fadedById={getFadedIdentityIds()}
            onRowHoverEnter={handleIdentityRowHoverEnter}
            onRowHoverLeave={handleIdentityRowHoverLeave}
            onRowClick={handleIdentityRowClick}
            focusedRowId={focusedIdentityId}
            isInteractive={!(isTwoAxisFocus && focusOrigin === 'dataContext')}
            rowRefs={identityRowRefs}
            className="relative z-10 w-[160px] shrink-0 2xl:w-[192px]"
          />

          <div className="flex-1" />

          <div className="relative z-10 mr-2 flex w-[388px] shrink-0 flex-col 2xl:mr-[76px]">
            <DataContextColumn
              rows={activeDataContextRows}
              fadedById={getFadedDataContextIds()}
              formatNumber={formatNumber}
              onRowHoverEnter={handleDataContextRowHoverEnter}
              onRowHoverLeave={handleDataContextRowHoverLeave}
              onRowClick={handleDataContextRowClick}
              focusedRowId={focusedDataContextId}
              isInteractive={!(isTwoAxisFocus && focusOrigin === 'identity')}
              rowRefs={dataRowRefs}
              onUserTypeClick={onUserTypeClick}
              noEventsIds={noEventsIds}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

AccessDiscoveryBody.displayName = 'AccessDiscoveryBody';
